import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Ruler,
  FileSpreadsheet,
  UsersRound,
  FileOutput,
  CloudCog,
} from "lucide-react";

const features = [
  {
    icon: ShieldCheck,
    title: "Bank-Grade Security",
    body: "CSRF protection, bcrypt password hashing, session hardening, and immutable audit logs on every action.",
  },
  {
    icon: Ruler,
    title: "Standards Compliant",
    body: "Built to Ind AS 116, IFRS 16, and SEBI valuation guidelines. Reviewed by a practicing Chartered Accountant.",
  },
  {
    icon: FileSpreadsheet,
    title: "Excel-Native Workflow",
    body: "Download templates, fill offline, upload data. Automatic validation and error highlighting — no manual re-entry.",
  },
  {
    icon: UsersRound,
    title: "Multi-User & Role-Based",
    body: "Owner, Admin, Editor, Viewer roles with approval workflows and threaded comments for full auditability.",
  },
  {
    icon: FileOutput,
    title: "Professional Exports",
    body: "Export to Excel, PDF reports, and Ind AS 116 financial statement notes — ready for sign-off and filing.",
  },
  {
    icon: CloudCog,
    title: "Cloud & Self-Hosted",
    body: "Deploy on any cPanel host. Multi-tenant workspace architecture. Your data stays in your environment.",
  },
];

export default function Benefits() {
  return (
    <section
      id="benefits"
      className="relative py-28 md:py-36 px-6 md:px-10 bg-white"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-gray-500 font-mono-x mb-4">
              §03 · Why Audivis
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-semibold text-[#202A36] leading-[1.02] tracking-[-0.03em]">
              Built with audit-grade
              <span className="text-gray-400"> precision.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-gray-600 leading-relaxed text-lg">
              The rigour you would apply across the audit table, encoded into
              every workflow. From CSRF to journal entries — defaults are
              correct, sensible and defensible.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.6,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative border-r border-b border-black/10 p-8 md:p-10 hover:bg-[#fafaf9] transition-colors"
                data-testid={`benefit-${i}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="h-11 w-11 rounded-xl bg-[#202A36] text-white flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <span className="font-mono-x text-[10px] uppercase tracking-[0.22em] text-gray-400">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl md:text-2xl font-semibold text-[#202A36] mb-3 tracking-tight">
                  {f.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[15px]">
                  {f.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
